import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { useCast } from "../cast/useCast";

interface VideoPlayerProps {
  streamUrl: string;
  posterUrl?: string | null;
  autoPlay?: boolean;
  onEnded?: () => void;
  loop?: boolean;
}

export function VideoPlayer({ streamUrl, posterUrl, autoPlay, onEnded, loop }: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [mode, setMode] = useState<"loading" | "original" | "hls" | "error">("loading");
  const [hlsUrl, setHlsUrl] = useState<string | null>(null);
  // Stalls/buffering surfaces an overlay so the user knows we're still
  // working on it (especially for live transcodes, where the first
  // fragment can take a few seconds to emerge from ffmpeg).
  const [buffering, setBuffering] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);

  const cast = useCast();

  useEffect(() => {
    let cancelled = false;
    setStreamError(null);
    setBuffering(false);

    async function probe() {
      try {
        // HEAD is cheap on the server (no ffmpeg spawn) and tells us which
        // mode to use without consuming any video bytes. We follow
        // redirects so a 302 to /stream/live/index.m3u8 lands us on the
        // playlist, and detect HLS by Content-Type (application/vnd.apple
        // .mpegurl). redirect:"manual" was previously used here but it
        // produces an opaqueredirect response — status==0, headers
        // inaccessible — so the 302 branch was dead and every transcode
        // asset got misrouted to <video src=/stream>.
        const r = await fetch(streamUrl, {
          method: "HEAD",
          credentials: "include",
        });
        if (cancelled) return;
        if (!r.ok) throw new Error(`unexpected status ${r.status}`);
        const ct = (r.headers.get("Content-Type") ?? "").toLowerCase();
        if (ct.includes("mpegurl") || ct.includes("m3u8")) {
          setHlsUrl(r.url);
          setMode("hls");
          return;
        }
        setMode("original");
      } catch (err) {
        if (!cancelled) setMode("error");
        console.error("video player probe failed", err);
      }
    }

    probe();
    return () => {
      cancelled = true;
    };
  }, [streamUrl]);

  useEffect(() => {
    if (mode !== "hls" || !hlsUrl || !videoRef.current) return;
    const v = videoRef.current;
    // Prefer HLS.js whenever it's supported (Chromium + Firefox always;
    // Safari supports both HLS.js and native). Only fall back to native
    // HLS when HLS.js can't run. Putting canPlayType first was a mistake:
    // modern Chromium with experimental HLS returns "maybe" for
    // application/vnd.apple.mpegurl and Chrome's built-in HLS demuxer
    // promptly chokes on our mpegts with "Parsed buffers not in DTS
    // sequence" even though the DTS values themselves are monotonic.
    if (!Hls.isSupported()) {
      v.src = hlsUrl;
      return;
    }
    // attachMedia → wait for MEDIA_ATTACHED → loadSource. The ERROR
    // listener surfaces fatal HLS.js failures (otherwise they're invisible
    // — the <video> element only fires error when the underlying
    // MediaSource bails, which leaves us guessing about playlist or
    // segment problems upstream).
    const hls = new Hls({
      enableWorker: true,
      lowLatencyMode: false,
      maxBufferLength: 30,
      maxMaxBufferLength: 60,
      // The segment route returns 503 + Retry-After while ffmpeg is
      // catching up to a seek target; let HLS.js retry harder than the
      // default 4 tries.
      fragLoadingMaxRetry: 10,
      fragLoadingRetryDelay: 500,
      fragLoadingMaxRetryTimeout: 8000,
    });
    hls.on(Hls.Events.MEDIA_ATTACHED, () => { hls.loadSource(hlsUrl); });
    hls.on(Hls.Events.ERROR, (_evt, data) => {
      console.error("hls.js error", {
        type: data.type,
        details: data.details,
        fatal: data.fatal,
        response: data.response && { code: data.response.code },
        frag: data.frag && { sn: data.frag.sn, url: data.frag.url },
      });
      if (data.fatal) {
        setStreamError(`HLS: ${data.details}`);
      }
    });
    hls.attachMedia(v);
    return () => hls.destroy();
  }, [mode, hlsUrl]);

  // Auto-play next on ended
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !onEnded) return;
    const h = () => onEnded();
    v.addEventListener("ended", h);
    return () => v.removeEventListener("ended", h);
  }, [onEnded, mode]);

  // Double-tap seek: tap right half twice → +10s, tap left half twice → -5s.
  // Single tap falls through to native play/pause behaviour. Tracks tap
  // time + side so two taps on the same side within 300ms count as a
  // double-tap, anything else resets.
  const lastTapRef = useRef<{ t: number; side: "left" | "right" } | null>(null);
  const [seekFlash, setSeekFlash] = useState<{ side: "left" | "right"; delta: number } | null>(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let touchStart: { x: number; y: number; t: number } | null = null;
    const onTs = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0]!;
      touchStart = { x: t.clientX, y: t.clientY, t: Date.now() };
    };
    const onTe = (e: TouchEvent) => {
      const start = touchStart;
      touchStart = null;
      if (!start) return;
      const t = e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      const dt = Date.now() - start.t;
      // Tap = small movement, short duration.
      if (Math.abs(dx) > 30 || Math.abs(dy) > 30 || dt > 300) {
        lastTapRef.current = null;
        return;
      }
      const rect = v.getBoundingClientRect();
      const mid = rect.left + rect.width / 2;
      const side: "left" | "right" = t.clientX < mid ? "left" : "right";
      const last = lastTapRef.current;
      if (last && Date.now() - last.t < 300 && last.side === side) {
        // Double-tap on the same side → seek.
        const delta = side === "right" ? 10 : -5;
        const target = Math.max(0, Math.min(v.duration || Infinity, v.currentTime + delta));
        v.currentTime = target;
        lastTapRef.current = null;
        setSeekFlash({ side, delta });
        // Auto-clear the flash overlay so it doesn't linger.
        setTimeout(() => setSeekFlash(null), 600);
        // Prevent the synthesized click that follows touchend, so we
        // don't also toggle play/pause from the double-tap.
        e.preventDefault();
      } else {
        lastTapRef.current = { t: Date.now(), side };
      }
    };
    v.addEventListener("touchstart", onTs, { passive: true });
    // touchend needs to be non-passive so we can preventDefault() to
    // suppress the click that follows a double-tap.
    v.addEventListener("touchend", onTe, { passive: false });
    return () => {
      v.removeEventListener("touchstart", onTs);
      v.removeEventListener("touchend", onTe);
    };
  }, [mode]);

  // Buffering overlay: <video> fires waiting/stalled when the source
  // can't keep up, and playing/canplay when it recovers. Hooked once
  // the video element exists; works the same for live-transcoded MP4
  // (where buffering is just "ffmpeg hasn't emitted enough yet") and
  // for cached HLS.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onWaiting = () => setBuffering(true);
    const onStalled = () => setBuffering(true);
    const onPlaying = () => setBuffering(false);
    const onCanPlay = () => setBuffering(false);
    const onError = () => {
      const err = v.error;
      const code = err?.code;
      // 4 = MEDIA_ERR_SRC_NOT_SUPPORTED — usually MSE rejecting a buffer
      // (codec/PTS/container mismatch) rather than the transcode itself
      // crashing. Log enough to actually diagnose; without this the UI
      // bubble was the only signal.
      console.error("video element error", {
        code, message: err?.message,
        networkState: v.networkState, readyState: v.readyState,
        currentTime: v.currentTime, src: v.currentSrc,
      });
      setStreamError(
        code === 4
          ? "The video stream ended unexpectedly (transcode failed)."
          : `Playback error (${code ?? "unknown"}).`,
      );
      setBuffering(false);
    };
    v.addEventListener("waiting", onWaiting);
    v.addEventListener("stalled", onStalled);
    v.addEventListener("playing", onPlaying);
    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("error", onError);
    return () => {
      v.removeEventListener("waiting", onWaiting);
      v.removeEventListener("stalled", onStalled);
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("error", onError);
    };
  }, [mode]);

  function castThis() {
    if (!cast.ready) return;
    const url = mode === "hls" && hlsUrl ? new URL(hlsUrl, window.location.origin).toString() : new URL(streamUrl, window.location.origin).toString();
    const contentType = mode === "hls" ? "application/x-mpegURL" : "video/mp4";
    cast.play({ url, contentType, title: "Media Viewer" });
  }

  if (mode === "loading") {
    return <div className="flex h-[60vh] items-center justify-center text-zinc-500">Probing stream…</div>;
  }
  if (mode === "error") {
    return <div className="flex h-[60vh] items-center justify-center text-red-300">Could not play this video.</div>;
  }

  return (
    <div className="relative inline-block max-w-full">
      <video
        ref={videoRef}
        src={mode === "original" ? streamUrl : undefined}
        poster={posterUrl ?? undefined}
        controls
        autoPlay={autoPlay ?? false}
        loop={loop ?? false}
        // "auto" keeps the browser buffering ahead even while paused;
        // "metadata" explicitly stops downloading after the first byte.
        // For HLS mode we don't set src on the element directly (HLS.js
        // manages MediaSource), so preload only meaningfully affects
        // mode === "original".
        preload={mode === "original" ? "auto" : "metadata"}
        className="max-h-[80vh] max-w-full"
      />
      {buffering && !streamError && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/40">
          <div className="flex items-center gap-3 rounded bg-black/70 px-4 py-2 text-sm text-white">
            <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            Buffering — preparing stream…
          </div>
        </div>
      )}
      {seekFlash && (
        <div
          className={
            "pointer-events-none absolute inset-y-0 flex w-1/2 items-center justify-center bg-black/30 text-3xl font-semibold text-white " +
            (seekFlash.side === "right" ? "right-0" : "left-0")
          }
        >
          {seekFlash.side === "right" ? "⏩ +10s" : "⏪ −5s"}
        </div>
      )}
      {streamError && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/60">
          <div className="max-w-md rounded bg-rose-900/80 px-4 py-3 text-sm text-rose-100 shadow-lg">
            {streamError}
            <div className="mt-1 text-xs text-rose-200/70">
              Try reloading; if it persists this codec/container may need
              the optimizer to re-encode it first.
            </div>
          </div>
        </div>
      )}
      {cast.available && (
        <button
          onClick={castThis}
          disabled={!cast.ready}
          title={cast.ready ? "Cast to a Chromecast device" : "Searching for Cast devices…"}
          className="absolute right-2 top-2 rounded bg-black/70 px-3 py-1 text-xs text-white hover:bg-black/90 disabled:opacity-50"
        >
          📺 {cast.casting ? "Casting" : "Cast"}
        </button>
      )}
    </div>
  );
}
